import { useForm, SubmitHandler, SubmitErrorHandler } from "react-hook-form";
import { scrollToFirstError } from "@/utils/scrollToFirstError";
import { useState, useEffect, useRef } from "react";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { FormSubmit } from "@/components/FormSubmit";
import { ArrowRightIcon } from "@/components/icons/icons";
import { api, UserCapabilitiesOut } from "@/services/api";
import { useErrorHandler } from "@/context/ErrorHandlerContext";
import { useNavigate } from "react-router-dom";
import { useUpdateOnboardingStep } from "@/hooks/useUpdateOnboardingStep";
import { useExpertSignupPrefill } from "@/hooks/useExpertSignupPrefill";
import { profileStepCompleted, profileCompleted } from "@/utils/tracking";
import { useAuth } from "@/hooks/useAuth";
import { getPrimaryRole } from "@/utils/roleUtils";
import { SecondaryButton } from "@/components/buttonVariants";
import { StarPortfolioField } from "@/components/StarPortfolioField";
import { CheckBox } from "@/components/CheckBox";
import { EXPERT_CONSENTS, emptyExpertConsents } from "@/constants/expertCapabilities";
import {
  StarPortfolioStory,
  cleanStarStories,
  emptyStarStory,
  isStarStoryComplete,
  isStarStoryWithinLimits,
  isStarStoryEmpty,
  toStarEntries,
} from "@/utils/starPortfolio";

const starPortfolioObjectSchema = z.object({
  // Validated in the superRefine below rather than here: "at least one story,
  // and every started story complete" is two rules about the array as a whole,
  // and expressing them per-element would put the error on the wrong field.
  starPortfolio: z.array(
    z.object({
      title: z.string().nullish(),
      situation: z.string(),
      task: z.string(),
      action: z.string(),
      result: z.string(),
    }),
  ),
});

const starPortfolioSchema = starPortfolioObjectSchema.superRefine((data, ctx) => {
  const started = data.starPortfolio.filter((story) => !isStarStoryEmpty(story));
  if (started.length === 0) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: "Please share at least one S.T.A.R. story",
      path: ["starPortfolio"],
    });
  } else if (!started.every(isStarStoryComplete)) {
    // The per-story field already names which parts are missing; this is the
    // form-level message that stops the submit.
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: "Please complete every part of each story you've started, or remove it.",
      path: ["starPortfolio"],
    });
  } else if (!started.every(isStarStoryWithinLimits)) {
    // The over-long field is flagged on its own as the user types; this stops
    // the submit so it never reaches the server.
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: "Please shorten the highlighted S.T.A.R. fields before continuing.",
      path: ["starPortfolio"],
    });
  }
});

type StarPortfolioFormInputs = z.infer<typeof starPortfolioSchema>;

/**
 * Hydrate the form from whatever the API holds.
 *
 * An entry written before the form had fields is one blob of text with no
 * parts to put in them. It is dropped from the editor rather than guessed at:
 * splitting it would show the user words they never wrote, in fields they'd
 * then have to correct. The blob is still stored and still displayed on the
 * profile -- it just isn't editable here until rewritten.
 */
const starPortfolioToStories = (value: unknown): StarPortfolioStory[] =>
  toStarEntries(value).filter((entry): entry is StarPortfolioStory => typeof entry !== "string");

interface StarPortfolioFormProps {
  /**
   * When true, this is mounted inside the achiever-side Expert Application
   * wizard -- the stories are saved as a draft and the wizard's own Review &
   * Submit step takes the acknowledgements and submits. Same pattern as
   * AdvisorPreferenceForm's `embedded` prop.
   */
  embedded?: boolean;
  formId?: string;
  onSubmitSuccess?: () => void;
  onLoadingChange?: (loading: boolean) => void;
}

/**
 * Expert signup step 4 ("STAR Portfolio") and Expert Application step 4.
 *
 * Standalone, this is the end of Expert signup: it takes the four
 * acknowledgements and sends the advisor preference, which is what submits
 * the application. Step 3 (Operator DNA) only saved a draft, so nothing is
 * submitted until the stories are in.
 */
export const StarPortfolioForm = ({
  embedded = false,
  formId,
  onSubmitSuccess,
  onLoadingChange,
}: StarPortfolioFormProps) => {
  const [loading, setLoading] = useState(false);
  // Everything step 3 saved, needed to create the advisor preference in one go.
  const [capabilities, setCapabilities] = useState<UserCapabilitiesOut | null>(null);
  const { user } = useAuth();
  const handleApiError = useErrorHandler();
  const navigate = useNavigate();
  const { updateStep } = useUpdateOnboardingStep();
  const formRef = useRef<HTMLFormElement>(null);

  const {
    handleSubmit,
    formState: { errors, isSubmitted },
    setValue,
    getValues,
    trigger,
    watch,
  } = useForm<StarPortfolioFormInputs>({
    resolver: zodResolver(starPortfolioSchema),
    // Errors only after a submit attempt, then live as the user fixes them.
    mode: "onSubmit",
    reValidateMode: "onChange",
    shouldFocusError: false,
    defaultValues: { starPortfolio: [emptyStarStory()] },
  });

  // Acknowledgements. Only asked on the standalone signup, where this step is
  // the end of the flow -- the apply-as-expert wizard takes the same four on
  // its own Review & Submit step.
  const [consents, setConsents] = useState<Record<string, boolean>>(emptyExpertConsents);
  const [consentError, setConsentError] = useState<string | null>(null);
  const allConsented = EXPERT_CONSENTS.every(({ key }) => consents[key]);

  useEffect(() => {
    onLoadingChange?.(loading);
  }, [loading, onLoadingChange]);

  // Set once saved stories have loaded; CRM prefill waits for it (below).
  const [hydrated, setHydrated] = useState(false);
  const [crmPrefilled, setCrmPrefilled] = useState(false);
  const crmAppliedRef = useRef(false);
  const { data: crmPrefill } = useExpertSignupPrefill(true);

  useEffect(() => {
    const hydrate = async () => {
      try {
        const caps = await api.capabilities.getMine();
        setCapabilities(caps);
        const stories = starPortfolioToStories(caps.starPortfolio);
        if (stories.length > 0) setValue("starPortfolio", stories);
        // Someone coming back to this step shouldn't have to re-tick what they
        // already agreed to.
        const saved = EXPERT_CONSENTS.filter(({ key }) => caps[key] === true).map(({ key }) => key);
        if (saved.length > 0) {
          setConsents((current) => ({
            ...current,
            ...Object.fromEntries(saved.map((key) => [key, true])),
          }));
        }
      } catch {
        // No capabilities row yet — form starts with an empty story.
      } finally {
        setHydrated(true);
      }
    };
    hydrate();
  }, [setValue]);

  // Prefill from the Expert's CRM record when no story has been saved yet --
  // never over stories they wrote (see UserProfileForm).
  useEffect(() => {
    if (!hydrated || !crmPrefill || crmAppliedRef.current) return;
    crmAppliedRef.current = true;
    const hasStory = getValues("starPortfolio").some((story) => !isStarStoryEmpty(story));
    if (hasStory || crmPrefill.starPortfolio.length === 0) return;
    setValue("starPortfolio", crmPrefill.starPortfolio, { shouldDirty: true });
    setCrmPrefilled(true);
  }, [hydrated, crmPrefill, getValues, setValue]);

  const starPortfolio = watch("starPortfolio") || [emptyStarStory()];

  const CONSENT_ERROR = "Please agree to all four acknowledgements to finish signing up";

  const onInvalid: SubmitErrorHandler<StarPortfolioFormInputs> = () => {
    if (!embedded && !allConsented) setConsentError(CONSENT_ERROR);
    scrollToFirstError(formRef.current);
  };

  const onsubmit: SubmitHandler<StarPortfolioFormInputs> = async (data) => {
    if (!embedded && !allConsented) {
      setConsentError(CONSENT_ERROR);
      scrollToFirstError(formRef.current);
      return;
    }
    setConsentError(null);
    setLoading(true);
    try {
      const validStories = cleanStarStories(data.starPortfolio);

      if (embedded) {
        await api.capabilities.saveDraft({ starPortfolio: validStories });
        onSubmitSuccess?.();
        return;
      }

      const id = (await api.advisors.getAdvisor()).userId;
      const consentFields = {
        ...Object.fromEntries(EXPERT_CONSENTS.map(({ key }) => [key, consents[key]])),
        // profileBuildConsented has no checkbox of its own -- it restates the
        // first acknowledgement, and the apply-as-expert flow sends it the same
        // way.
        profileBuildConsented: true,
      };

      let preferenceExists = false;
      try {
        preferenceExists = !!(await api.advisors.getPreference(id));
      } catch {
        // No preference yet — first time through, created below.
      }

      // Either call submits the application (SUBMITTED, once). The update is
      // partial, so it only needs what this step adds; the create replaces the
      // whole questionnaire, so it re-sends what step 3 saved as a draft.
      if (preferenceExists) {
        await api.advisors.updatePreference(id, { starPortfolio: validStories, ...consentFields });
      } else {
        const caps = capabilities;
        await api.advisors.createPreference(id, {
          advisoryAreas: caps?.coachingStrengths ?? [],
          advisoryStyles: caps?.advisoryStyles ?? [],
          functionsAdvised: caps?.functionsAdvised ?? [],
          levelsMentored: caps?.levelsMentored ?? [],
          advisoryScenarios: caps?.advisoryScenarios ?? undefined,
          advisoryAccomplishments: caps?.advisoryAccomplishments ?? undefined,
          interestMultiAdvisorBoards: caps?.interestMultiAdvisorBoards ?? undefined,
          createLeadPrograms: caps?.createLeadPrograms,
          joinAsCoExpert: caps?.joinAsCoExpert,
          joinPabBoard: caps?.joinPabBoard,
          peerAdvisory: caps?.peerAdvisory,
          coFounding: caps?.coFounding,
          coreIndustries: caps?.coreIndustries ?? undefined,
          operatorThesis: caps?.operatorThesis ?? undefined,
          problemAndInsight: caps?.problemAndInsight ?? undefined,
          advisoryPhilosophy: caps?.advisoryPhilosophy ?? undefined,
          engagementStructure: caps?.engagementStructure ?? undefined,
          rate: caps?.rate ?? undefined,
          starPortfolio: validStories,
          ...consentFields,
          days: [],
          timeSlots: [],
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC",
        });
      }

      if (user) {
        profileStepCompleted({
          step_name: "star_portfolio_completed",
          user_role: getPrimaryRole(user),
        });

        const signupTimestamp = localStorage.getItem("signup_timestamp");
        const timeToCompleteSeconds = signupTimestamp
          ? Math.floor((Date.now() - parseInt(signupTimestamp)) / 1000)
          : 0;

        profileCompleted({
          time_to_complete_seconds: timeToCompleteSeconds,
          user_role: user.role,
        });

        localStorage.removeItem("signup_timestamp");
      }

      await updateStep("complete");
      navigate("/home");
    } catch (err: unknown) {
      handleApiError(err, starPortfolioObjectSchema);
    } finally {
      setLoading(false);
    }
  };

  return (
    <form
      ref={formRef}
      id={formId}
      onSubmit={handleSubmit(onsubmit, onInvalid)}
      className="flex flex-col gap-6 font-inter"
    >
      {/* Heading */}
      {!embedded && (
        <div className="flex flex-col gap-2">
          <h1 className="text-[28px] font-bold leading-tight tracking-[0.3px] text-theme-text-heading">
            Expert Contribution
          </h1>
          <p className="text-18 text-preference-card-subtitle leading-7 tracking-[-0.44px]">
            Tell us how you want to contribute as an Expert
          </p>
        </div>
      )}

      {crmPrefilled && (
        <p className="rounded-[10px] border border-theme-border bg-theme-bg-surface px-4 py-3 text-14 text-theme-text-secondary">
          We&apos;ve pre-filled your S.T.A.R. stories from your Expert application. Please review
          each part before continuing.
        </p>
      )}

      {/* S.T.A.R. Portfolio (repeatable, 1-3 stories) */}
      <StarPortfolioField
        value={starPortfolio}
        onChange={(stories) => {
          setValue("starPortfolio", stories);
          if (isSubmitted) trigger("starPortfolio");
        }}
        error={errors.starPortfolio?.message}
        showErrors={isSubmitted}
      />

      {/* Acknowledgements -- standalone signup only. Inside the apply-as-expert
          wizard these are taken on its Review & Submit step, and showing them
          twice would ask for the same consent twice. */}
      {!embedded && (
        <div
          data-field
          className="flex flex-col gap-3 rounded-2xl border border-theme-form-panel-border bg-theme-form-panel-bg p-6"
        >
          <h2 className="text-16 font-semibold text-theme-text-heading">Acknowledgements</h2>
          <p className="-mt-2 text-12 leading-4 text-theme-text-secondary">
            Please confirm all four before finishing.
          </p>
          {EXPERT_CONSENTS.map(({ key, label }) => (
            <CheckBox
              key={key}
              id={key}
              checked={Boolean(consents[key])}
              onChange={(checked) => {
                setConsents((current) => ({ ...current, [key]: checked }));
                if (consentError) setConsentError(null);
              }}
            >
              {label}
            </CheckBox>
          ))}
          {consentError && (
            <p data-field-error className="text-12 text-error-500">
              {consentError}
            </p>
          )}
        </div>
      )}

      {/* Buttons */}
      {!embedded && (
        <div className="flex items-stretch gap-4 pt-2 h-12 md:h-[58px]">
          <SecondaryButton
            className="text-14 md:text-16 text-back-btn-text text-center font-inter font-semibold"
            onClick={() => navigate("/signup/3")}
          >
            Back
          </SecondaryButton>
          <div className="flex-1">
            <FormSubmit
              isLoading={loading}
              disabled={loading}
              variant="primaryGradient"
              className="h-14 text-14 md:text-16"
              rightIcon={<ArrowRightIcon className="text-white" />}
            >
              Complete Sign Up
            </FormSubmit>
          </div>
        </div>
      )}
    </form>
  );
};
